// @givmo/board — reading a board's compensation decisions (CBG-YR4-PAY, CBG-YR4-PAY-CONSENT; the
// backend's `agent_docs/contracts/board-compensation-decisions.md` §8).
//
// ══ WHAT THESE READS ARE ════════════════════════════════════════════════════════════════════════
//
// A decision is the board's own record of whose pay it set, what it relied on and how it got it —
// on a carried motion (frame c1), on a written consent, or labelling a record already in the minute
// book (an index entry). The reads serve each one in the regulation's order (§53.4958-6(c)(3)(i)):
// the decision and its date, who took part, what the board relied on, and when it was written down.
// Every sentence that concludes a part is the backend's (`written_down.statement`, the regulation's
// line, an index entry's `provenance_statement`, the board's own recorded determination), printed as
// served. The web composes only the plain lists around them: who was present, the counts, the names.
//
// ⚠ NOTHING HERE READS AN AMOUNT, COMPARES ANYTHING OR GRADES A DECISION. There is no figure on the
// wire to read, and the page draws no mark of whether a decision "has" what the regulation lists: an
// absent part is simply absent (the contract's §9; the derivation §4.5).
//
// Tolerant, as every Board decoder is: a decision missing its id, person or anchor is dropped; a
// missing list reads as none; a missing flag reads as the cautious answer (`editable` false, so no
// act the backend might refuse is offered); a value this build doesn't know is shown as it came.

import type {
  BoardCompensationDecisionFactsOut,
  BoardCompensationDecisionOut,
  BoardCompensationDocumentOut,
  BoardCompensationPaperOut,
  BoardCompensationPersonOut,
  BoardCompensationPresentOut,
  BoardCompensationReliedOnOut,
  BoardCompensationWhoTookPartOut,
  BoardCompensationWrittenDownOut,
  BoardDirectorConsentCompensationOut,
  BoardDirectorCompensationDocumentOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { displayLongDate } from '../../lib/display';
import { voteCastLabel } from '../in-meeting/in-meeting-decode';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { compensationWords as w } from './compensation-words';

export type Form990Line = '15a' | '15b';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function arrayOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value.filter(isObject) as T[]) : [];
}

/** The organization's own Form 990 answer, when the wire carries one this build knows. */
export function form990LineOf(value: unknown): Form990Line | null {
  return value === '15a' || value === '15b' ? value : null;
}

// ── One decision ───────────────────────────────────────────────────────────────────────────────

function decodeDocument(value: unknown): BoardCompensationDocumentOut | null {
  if (!isObject(value) || typeof value.id !== 'string' || text(value.file_name) === null) {
    return null;
  }
  return value as unknown as BoardCompensationDocumentOut;
}

function decodeReliedOn(value: unknown): BoardCompensationReliedOnOut | null {
  if (!isObject(value)) return null;
  return {
    documents: (Array.isArray(value.documents) ? value.documents : [])
      .map(decodeDocument)
      .filter((row): row is BoardCompensationDocumentOut => row !== null),
    how_obtained: text(value.how_obtained),
    reason_outside_range: text(value.reason_outside_range),
  };
}

function decodeWhoTookPart(value: unknown): BoardCompensationWhoTookPartOut | null {
  if (!isObject(value)) return null;
  return {
    ...(value as BoardCompensationWhoTookPartOut),
    present: arrayOf<BoardCompensationPresentOut>(value.present).filter(
      (row) => text(row.display_name) !== null,
    ),
    recused: arrayOf<{ display_name: string; basis_note?: string | null }>(value.recused).filter(
      (row) => text(row.display_name) !== null,
    ),
    casts: arrayOf<{ display_name: string; vote_cast: string }>(value.casts).filter(
      (row) => text(row.display_name) !== null,
    ),
    signers: Array.isArray(value.signers)
      ? arrayOf<{ display_name: string }>(value.signers).filter(
          (row) => text(row.display_name) !== null,
        )
      : null,
  };
}

/** One decision, or null when it lacks what every part needs (its id, its person, its anchor). */
export function decodeDecision(value: unknown): BoardCompensationDecisionOut | null {
  if (
    !isObject(value) ||
    typeof value.id !== 'string' ||
    text(value.person_name) === null ||
    typeof value.anchor_kind !== 'string'
  ) {
    return null;
  }
  return {
    ...(value as unknown as BoardCompensationDecisionOut),
    position: typeof value.position === 'string' ? value.position : '',
    sort_order: count(value.sort_order) ?? 0,
    form_990_line: form990LineOf(value.form_990_line),
    decision: isObject(value.decision) ? (value.decision as BoardCompensationDecisionFactsOut) : {},
    who_took_part: decodeWhoTookPart(value.who_took_part),
    relied_on: decodeReliedOn(value.relied_on),
    written_down: isObject(value.written_down)
      ? (value.written_down as BoardCompensationWrittenDownOut)
      : {},
    // The cautious reading of a flag that didn't come: no act is offered that the backend might
    // refuse.
    editable: value.editable === true,
    labels_editable: value.labels_editable === true,
  };
}

function decisionsOf(value: unknown): BoardCompensationDecisionOut[] {
  return (Array.isArray(value) ? value : [])
    .map(decodeDecision)
    .filter((row): row is BoardCompensationDecisionOut => row !== null);
}

// ── The list (frame c2) ────────────────────────────────────────────────────────────────────────

/** The people on the list, each with their decisions newest first: the backend's order, kept. */
export function extractPeople(body: unknown): BoardCompensationPersonOut[] {
  return extractList<BoardCompensationPersonOut>(body, 'data')
    .filter((row) => isObject(row) && text(row.person_name) !== null)
    .map((row) => ({
      ...row,
      group_key: typeof row.group_key === 'string' ? row.group_key : row.person_name,
      position: typeof row.position === 'string' ? row.position : '',
      form_990_line: form990LineOf(row.form_990_line),
      last_decided_year: count(row.last_decided_year),
      decisions: decisionsOf(row.decisions),
    }))
    .filter((row) => row.decisions.length > 0);
}

// ── A meeting's decisions and papers (frame c1) ────────────────────────────────────────────────

export interface MeetingCompensation {
  /** Whether the details can still change: no minutes of the meeting approved. */
  recordOpen: boolean;
  /** The meeting's uploaded papers, in the backend's order: what a decision may name. */
  papers: BoardCompensationPaperOut[];
  decisions: BoardCompensationDecisionOut[];
}

export function decodeMeetingCompensation(body: unknown): MeetingCompensation {
  const value = isObject(body) ? body : {};
  return {
    recordOpen: value.record_open === true,
    papers: arrayOf<BoardCompensationPaperOut>(value.papers).filter(
      (row) => typeof row.id === 'string' && text(row.file_name) !== null,
    ),
    decisions: decisionsOf(value.decisions),
  };
}

/** One motion's decisions, in the order the officer added them (`sort_order`). */
export function decisionsForMotion(
  meeting: MeetingCompensation | null,
  motionId: string,
): BoardCompensationDecisionOut[] {
  return (meeting?.decisions ?? [])
    .filter((row) => row.decision.motion_id === motionId)
    .sort((a, b) => a.sort_order - b.sort_order);
}

// ── A consent's decisions (PAY-CONSENT) ────────────────────────────────────────────────────────

export interface ConsentCompensation {
  status: string;
  /** Whether the details can still change: the consent is a draft. */
  editable: boolean;
  decisions: BoardCompensationDecisionOut[];
  /** The ONE block all its decisions print as, which the consent's text must carry; null without. */
  textBlock: string | null;
  textCarriesBlock: boolean;
}

export function decodeConsentCompensation(body: unknown): ConsentCompensation {
  const value = isObject(body) ? body : {};
  return {
    status: typeof value.consent_status === 'string' ? value.consent_status : '',
    editable: value.editable === true,
    decisions: decisionsOf(value.decisions).sort((a, b) => a.sort_order - b.sort_order),
    textBlock: text(value.text_block),
    textCarriesBlock: value.text_carries_block === true,
  };
}

/** The director's consent read's decisions, in order, each with the documents it names. */
export function directorConsentDecisions(consent: unknown): BoardDirectorConsentCompensationOut[] {
  if (!isObject(consent)) return [];
  return arrayOf<BoardDirectorConsentCompensationOut>(consent.compensation_decisions)
    .filter((row) => typeof row.decision_id === 'string' && text(row.person_name) !== null)
    .map((row) => ({
      ...row,
      position: typeof row.position === 'string' ? row.position : '',
      documents: arrayOf<BoardDirectorCompensationDocumentOut>(row.documents).filter(
        (doc) => typeof doc.id === 'string' && text(doc.file_name) !== null,
      ),
    }))
    .filter((row) => row.documents.length > 0);
}

// ── The lines a card composes around the served sentences ──────────────────────────────────────

/** "September 10, 2026", or the dash's absence: a sentence chooses its own words without a date. */
export function longDay(iso: string | null | undefined): string | null {
  return displayLongDate(iso);
}

/**
 * Who was in the room, as the minutes name an attendee (approved, CBG-YR3-ROLES-PRESIDING): the
 * titles and "presiding" in parentheses after the name; someone recorded as not a director says so
 * (approved, CBG-YR3-ROLES), as frame c2 draws it.
 */
export function presentLine(rows: readonly BoardCompensationPresentOut[]): string {
  return rows
    .map((row) => {
      const said = [
        ...(row.title_labels ?? []).filter((label) => text(label) !== null),
        ...(row.presided === true ? [w.presiding] : []),
      ].join(', ');
      if (row.is_director === false) {
        return `${row.display_name} (${said ? roles.titlesNotDirector(said) : w.notADirector})`;
      }
      return said ? `${row.display_name} (${said})` : row.display_name;
    })
    .join(', ');
}

/** "4 for, 0 against, 0 abstaining. Recorded outcome: carried." — or null with no counts served. */
export function voteLine(who: BoardCompensationWhoTookPartOut | null): string | null {
  if (who === null) return null;
  const votesFor = count(who.votes_for);
  const against = count(who.votes_against);
  const abstaining = count(who.votes_abstaining);
  const outcome = text(who.outcome);
  const parts = [
    votesFor !== null && against !== null && abstaining !== null
      ? w.tally(votesFor, against, abstaining)
      : null,
    outcome !== null ? w.outcome(outcome) : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' ') : null;
}

/** Each seat's own vote, where one was recorded per seat: "For: A, B", "Against: C". */
export function castLines(who: BoardCompensationWhoTookPartOut | null): string[] {
  const casts = who?.casts ?? [];
  // The three known casts in the minutes' order, then any value this build doesn't know, as it came.
  const groups = [...new Set(['for', 'against', 'abstain', ...casts.map((row) => row.vote_cast)])];
  return groups
    .map((cast) => {
      const names = casts.filter((row) => row.vote_cast === cast).map((row) => row.display_name);
      return names.length > 0 ? `${voteCastLabel(cast)}: ${names.join(', ')}` : null;
    })
    .filter((line): line is string => line !== null);
}

/**
 * The committee whose decision it is (CBG-FIX87), as the decision's own record names it — a committee
 * meeting's minutes, a committee's filed consent — never the registry's name now; null for the
 * board's, and on an older backend that serves no `committee`.
 */
export function decisionCommittee(
  decision: BoardCompensationDecisionOut,
): { committeeId: string; name: string } | null {
  const served: unknown = (decision as { committee?: unknown }).committee;
  if (typeof served !== 'object' || served === null) return null;
  const row = served as Record<string, unknown>;
  const committeeId = text(row.committee_id);
  const name = text(row.name);
  return committeeId !== null && name !== null ? { committeeId, name } : null;
}

/** What a card is headed by: what the decision was made in, and when (and how it ended); a
 *  committee's decision names the committee first (CBG-FIX87). */
export function cardHead(decision: BoardCompensationDecisionOut): {
  title: string;
  when: string | null;
} {
  const facts = decision.decision;
  const day = longDay(decision.decided_on ?? facts.occurred_on);
  const committee = decisionCommittee(decision);
  // The committee first, unless the title already names it ("Meeting of the Finance Committee").
  const named = (title: string) =>
    committee !== null && !title.includes(committee.name)
      ? w.committeeHead(committee.name, title)
      : title;
  if (decision.anchor_kind === 'written_consent') {
    return {
      title: named(text(facts.consent_title) ?? w.title),
      when: day !== null ? `${day} · ${w.byWrittenConsent}` : w.byWrittenConsent,
    };
  }
  if (decision.anchor_kind === 'record') {
    return { title: text(facts.record_title) ?? w.title, when: day };
  }
  const outcome = text(facts.outcome);
  return {
    title: named(text(facts.meeting_title) ?? w.title),
    when: [day, outcome].filter((part): part is string => part !== null).join(' · ') || null,
  };
}

/**
 * An earlier decision as one line: where it came from, as the backend says it for one from the
 * minute book (its `provenance_statement`, AS SERVED — review-176's P3), else what it was made in
 * and the day.
 */
export function earlierLine(decision: BoardCompensationDecisionOut): string {
  if (decision.anchor_kind === 'record') {
    return (
      text(decision.decision.provenance_statement) ??
      w.recordedIn(text(decision.decision.record_title) ?? w.title)
    );
  }
  const { title, when } = cardHead(decision);
  const day = longDay(decision.decided_on);
  return day !== null ? `${title} · ${day}` : (when ?? title);
}

/** A decision row's quiet line under its name, on the motion card and the consent's panel: how
 *  many documents it names, or nothing. */
export function decisionFacts(decision: BoardCompensationDecisionOut): string | null {
  const documents = decision.relied_on?.documents?.length ?? 0;
  return documents > 0 ? w.documentCount(documents) : null;
}
